
'use client';

import { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Thermometer,
  Wind,
  Droplets,
  Eye,
  Compass,
  AlertTriangle,
  Video,
  Sun,
  Cloudy,
  CloudSun,
  CloudRain,
  Umbrella,
  Info,
  Maximize,
} from 'lucide-react';
import {
  liveWeatherSummary,
  LiveWeatherSummaryOutput,
  ForecastDay,
} from '@/ai/flows/live-weather-summary';
import { useToast } from '@/hooks/use-toast';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

function getWeatherIcon(summary: string, isRaining: boolean): React.ReactNode {
  const lowerCaseSummary = summary.toLowerCase();

  if (isRaining) {
    return <CloudRain className="w-16 h-16 text-primary" />;
  }
  if (
    lowerCaseSummary.includes('cloudy') ||
    lowerCaseSummary.includes('overcast')
  ) {
    if (
      lowerCaseSummary.includes('partly') ||
      lowerCaseSummary.includes('few') ||
      lowerCaseSummary.includes('scattered')
    ) {
      return <CloudSun className="w-16 h-16 text-primary" />;
    }
    return <Cloudy className="w-16 h-16 text-primary" />;
  }
  if (lowerCaseSummary.includes('clear') || lowerCaseSummary.includes('sunny')) {
    return <Sun className="w-16 h-16 text-primary" />;
  }

  return <Thermometer className="w-16 h-16 text-primary" />;
}

const ForecastIcon = ({ iconUrl, alt }: { iconUrl: string; alt: string }) => {
  if (!iconUrl) return <Sun className="w-8 h-8 text-primary" />;

  // The NWS API returns SVG icons with size parameters in the URL,
  // but they are often too small. We can request a larger size.
  const largeIconUrl = iconUrl.replace(/size=medium/g, 'size=large');

  return (
    <Image
      src={largeIconUrl}
      alt={alt}
      width={32}
      height={32}
      className="mx-auto"
      unoptimized
    />
  );
};

const ForecastDetailsModal = ({
  day,
  isOpen,
  onClose,
}: {
  day: ForecastDay | null;
  isOpen: boolean;
  onClose: () => void;
}) => {
  if (!day) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-4">
            <ForecastIcon iconUrl={day.icon} alt={day.shortForecast} />
            <div>
              Forecast for {day.name}
              <DialogDescription className="mt-1">
                High: {day.highTemp}°F / Low: {day.lowTemp}°F
              </DialogDescription>
            </div>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          <div className="prose prose-sm max-w-none text-muted-foreground dark:prose-invert">
            <p>{day.detailedForecast}</p>
          </div>
          <Separator />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div className="flex items-center gap-2 p-2 bg-secondary rounded-md">
              <Wind className="w-5 h-5 text-primary" />
              <div>
                <div className="font-semibold">Wind</div>
                <div>
                  {day.windSpeed} from {day.windDirection}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 p-2 bg-secondary rounded-md">
              <Umbrella className="w-5 h-5 text-primary" />
              <div>
                <div className="font-semibold">Precipitation</div>
                <div>{day.precipitationChance ?? 0}% chance</div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function WeatherCard({ className }: { className?: string }) {
  const [weather, setWeather] = useState<LiveWeatherSummaryOutput | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [parent, setParent] = useState<string | null>(null);
  const [selectedDay, setSelectedDay] = useState<ForecastDay | null>(null);
  const cameraContainerRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  const handleFullscreen = () => {
    if (cameraContainerRef.current) {
      if (cameraContainerRef.current.requestFullscreen) {
        cameraContainerRef.current.requestFullscreen();
      } else if ((cameraContainerRef.current as any).webkitRequestFullscreen) {
        (cameraContainerRef.current as any).webkitRequestFullscreen();
      } else if ((cameraContainerRef.current as any).msRequestFullscreen) {
        (cameraContainerRef.current as any).msRequestFullscreen();
      }
    }
  };

  useEffect(() => {
    // Twitch requires the parent hostname for embedding.
    // This will only be available on the client-side.
    setParent(window.location.hostname);

    async function fetchWeather() {
      try {
        setLoading(true);
        setError(null);
        const result = await liveWeatherSummary({});
        setWeather(result);
      } catch (e) {
        console.error(e);
        const errorMessage =
          e instanceof Error ? e.message : 'An unknown error occurred.';
        setError(errorMessage);
        toast({
          variant: 'destructive',
          title: 'Failed to load weather',
          description: errorMessage,
        });
      } finally {
        setLoading(false);
      }
    }
    fetchWeather();
  }, [toast]);

  const renderContent = () => {
    if (loading) {
      return <WeatherSkeleton className={className} />;
    }

    if (error || !weather) {
      return (
        <>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Thermometer className="w-5 h-5 text-muted-foreground" />
              Live Weather
            </CardTitle>
            <CardDescription>SRAC Flying Field</CardDescription>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>
                Could not fetch live weather data. Please try again later.
              </AlertDescription>
            </Alert>
          </CardContent>
        </>
      );
    }

    const WeatherIcon = getWeatherIcon(weather.summary, weather.rain);

    return (
      <>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Thermometer className="w-5 h-5 text-primary" />
            Live Weather
          </CardTitle>
          <CardDescription>SRAC Flying Field Conditions</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="p-4 bg-secondary rounded-lg text-secondary-foreground space-y-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-4">
                {WeatherIcon}
                <div className="text-5xl font-bold">{weather.temperature}°F</div>
              </div>
              <div className="text-right">
                <div className="font-semibold">{weather.summary}</div>
                <div className="text-xs">
                  {weather.rain ? 'Raining' : 'Dry'}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center text-xs">
              <div className="flex flex-col items-center gap-1">
                <Wind className="w-5 h-5" />
                <span className="font-semibold">{weather.windSpeed} mph</span>
              </div>
              <div className="flex flex-col items-center gap-1">
                <Compass className="w-5 h-5" />
                <span className="font-semibold">{weather.windDirection}</span>
              </div>
              <div className="flex flex-col items-center gap-1">
                <Droplets className="w-5 h-5" />
                <span className="font-semibold">{weather.humidity}%</span>
              </div>
              <div className="flex flex-col items-center gap-1">
                <Eye className="w-5 h-5" />
                <span className="font-semibold">{weather.visibility} mi</span>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            {weather.detailedSummary && (
              <p className="text-sm text-muted-foreground leading-relaxed flex items-start gap-2">
                <Info className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                <span>{weather.detailedSummary}</span>
              </p>
            )}

            {weather.forecast && weather.forecast.length > 0 && (
              <>
                <Separator />
                <div className="space-y-2">
                   <h4 className="text-sm font-semibold text-foreground">5-Day Forecast</h4>
                    <div className="grid grid-cols-5 gap-2 text-center">
                        {weather.forecast.map((day) => (
                        <div
                            key={day.name}
                            onClick={() => setSelectedDay(day)}
                            className="p-2 rounded-md hover:bg-secondary cursor-pointer transition-colors"
                        >
                            <div className="font-semibold text-xs">{day.name}</div>
                            <ForecastIcon iconUrl={day.icon} alt={day.shortForecast} />
                            <div className="text-xs mt-1">
                                <span className="font-bold text-foreground">{day.highTemp}°</span>
                                <span className="text-muted-foreground">/{day.lowTemp}°</span>
                            </div>
                        </div>
                        ))}
                    </div>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </>
    );
  };

  return (
    <>
        <Card className={`${className} flex flex-col`} id="weather">
        {renderContent()}
        <CardHeader className="pt-0">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Video className="w-5 h-5 text-primary" />
                  Live Field Camera
                </CardTitle>
                <CardDescription>
                  See what's happening at the field right now.
                </CardDescription>
              </div>
              {parent && (
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleFullscreen}
                  title="View fullscreen"
                >
                  <Maximize className="w-4 h-4" />
                </Button>
              )}
            </div>
        </CardHeader>
        <CardContent className="flex-grow flex flex-col">
            <div
              ref={cameraContainerRef}
              className="aspect-video w-full overflow-hidden rounded-lg border flex-grow bg-black"
            >
            {parent ? (
                <iframe
                src={`https://player.twitch.tv/?channel=sracadmin&parent=${parent}&muted=true&autoplay=true&parent=sracinc.com&parent=www.sracinc.com&parent=www.sracinc.org&parent=sracinc.org`}
                height="100%"
                width="100%"
                allowFullScreen={true}
                title="SRAC Live Field Camera"
                ></iframe>
            ) : (
                <Skeleton className="w-full h-full" />
            )}
            </div>
        </CardContent>
        </Card>
        
        <ForecastDetailsModal 
            isOpen={selectedDay !== null}
            onClose={() => setSelectedDay(null)}
            day={selectedDay}
        />
    </>
  );
}

function WeatherSkeleton({ className }: { className?: string }) {
  return (
    <Card className={className}>
      <CardHeader>
        <Skeleton className="h-6 w-3/5" />
        <Skeleton className="h-4 w-2/5" />
      </CardHeader>
      <CardContent className="space-y-6">
        <Skeleton className="h-28 w-full" />
        <div className="space-y-2">
          <Skeleton className="h-4 w-full" />
        </div>
        <Separator />
        <div className="grid grid-cols-5 gap-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex flex-col items-center gap-2">
              <Skeleton key={`sk-day-${i}`} className="h-4 w-10" />
              <Skeleton key={`sk-icon-${i}`} className="h-8 w-8 rounded-full" />
              <Skeleton key={`sk-temp-${i}`} className="h-4 w-10" />
            </div>
          ))}
        </div>
      </CardContent>
      <CardHeader className="pt-0">
        <Skeleton className="h-6 w-3/5" />
        <Skeleton className="h-4 w-2/5" />
      </CardHeader>
      <CardContent>
        <Skeleton className="aspect-video w-full" />
      </CardContent>
    </Card>
  );
}

    